import React from "react";
import * as Sentry from "@sentry/browser";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { intersection } from "lodash";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { ensureError } from "../../commons/libs/error";
import envelope from "../../commons/libs/externals/envelope";
import { getClientVersion } from "../../commons/libs/externals/versions";
import type {
  AdminDebugCredentials,
  AuthenticationCredentials,
  RegistrationCredentials
} from "../../commons/types/credentials";
import { Locale } from "../../commons/types/localization";
import * as translations from "../../resources/translations/mobile-resources.json";
import config from "../commons/config";
import { TrackingProvider } from "../commons/container/utils/tracking-context";
import { changeLanguage, initI18n } from "../commons/libs/externals/i18n";
import initSentry from "../commons/libs/externals/sentry";
import sentryRootOptions from "../commons/libs/externals/sentry-root-options";
import { shouldRetryQuery } from "../commons/libs/hooks/use-query";

import App from "./container/App/App";
import CredentialsNotice from "./container/CredentialsNotice/CredentialsNotice";
import MaxLicensesReachedNotice from "./container/MaxLicensesReachedNotice/MaxLicensesReachedNotice";
import { hasAdminDebugCookie, setAdminDebugCookie, setLicenseCookies } from "./libs/cookies";
import {
  getQueriedAdminDebugCredentials,
  getQueriedAuthenticationCredentials,
  getQueriedRegistrationCredentials,
  getStoredAuthenticationCredentials,
  getStoredRegistrationCredentials,
  hasAdminDebugCredentials,
  hasExistingAuthenticationCredentials,
  hasNewRegistrationCredentials,
  isAuthenticated,
  isTryingToRegister,
  registerLicense,
  reloadApp,
  removeRegistrationCredentials,
  storeAuthenticationCredentials,
  storeRegistrationCredentials,
  storeServiceTag
} from "./libs/credentials";
import initMixpanel from "./libs/externals/mixpanel";
import { generateManifest, injectManifest } from "./manifest";
import * as allActions from "./store/actions";
import { createMobileTrackingMiddleware } from "./store/middlewares/tracking";
import createMobileStore from "./store/store";

enum AuthenticationState {
  Authenticated,
  Registering,
  RegistrationFailed,
  InsufficientCredentials
}

const sentry = initSentry(Sentry);

// Initialize before the authentication flow below, which can render without ever reaching `renderRoot`'s tail.
sentry.initSentry({
  release: getClientVersion(),
  environment: envelope.isProductionTracking ? "production" : "development",
  ...config.mobile.sentry
});

const authenticate = async (
  queriedRegistrationCredentials: Partial<RegistrationCredentials>,
  storedRegistrationCredentials: Partial<RegistrationCredentials>,
  queriedAuthenticationCredentials: Partial<AuthenticationCredentials>,
  storedAuthenticationCredentials: Partial<AuthenticationCredentials>,
  queriedAdminDebugCredentials: Partial<AdminDebugCredentials>
): Promise<AuthenticationState> => {
  if (hasAdminDebugCookie()) {
    return AuthenticationState.Authenticated;
  } else if (hasAdminDebugCredentials(queriedAdminDebugCredentials)) {
    // We cannot validate signature client-side without masterToken (which we must not expose)
    // Validation happens server-side via cookie; we simply persist cookie for middleware.
    const { adminDebug, serviceTag } = queriedAdminDebugCredentials;
    const [adminTsStr, adminSig] = adminDebug.split(".");
    const adminTs = parseInt(adminTsStr, 10);

    if (!adminTsStr || !adminSig || !serviceTag || isNaN(adminTs)) {
      return AuthenticationState.InsufficientCredentials;
    }

    setAdminDebugCookie(adminTs, adminSig);
    storeServiceTag(serviceTag);

    reloadApp();

    return AuthenticationState.Registering;
  } else if (hasNewRegistrationCredentials(queriedRegistrationCredentials)) {
    storeRegistrationCredentials(queriedRegistrationCredentials);

    reloadApp();

    return AuthenticationState.Registering;
  } else if (isTryingToRegister(storedRegistrationCredentials)) {
    try {
      const license = await registerLicense(storedRegistrationCredentials);

      removeRegistrationCredentials();
      storeAuthenticationCredentials({
        licenseId: String(license.id),
        licenseToken: license.licenseToken,
        serviceTag: storedRegistrationCredentials.serviceTag
      });
      reloadApp();

      return AuthenticationState.Registering;
    } catch (error) {
      const ensuredError = ensureError(error);
      if ("status" in ensuredError && ensuredError.status === 402) {
        return AuthenticationState.RegistrationFailed;
      } else {
        return AuthenticationState.InsufficientCredentials;
      }
    }
  } else if (hasExistingAuthenticationCredentials(queriedAuthenticationCredentials)) {
    storeAuthenticationCredentials(queriedAuthenticationCredentials);
    reloadApp();

    return AuthenticationState.Registering;
  } else if (isAuthenticated(storedAuthenticationCredentials)) {
    return AuthenticationState.Authenticated;
  } else {
    return AuthenticationState.InsufficientCredentials;
  }
};

const renderRoot = async (): Promise<React.JSX.Element | null> => {
  if (envelope.isProductionBuild) {
    const queriedRegistrationCredentials = getQueriedRegistrationCredentials();
    const storedRegistrationCredentials = getStoredRegistrationCredentials();

    const queriedAuthenticationCredentials = getQueriedAuthenticationCredentials();
    const storedAuthenticationCredentials = getStoredAuthenticationCredentials();

    const queriedAdminDebugCredentials = getQueriedAdminDebugCredentials();

    const authenticationState = await authenticate(
      queriedRegistrationCredentials,
      storedRegistrationCredentials,
      queriedAuthenticationCredentials,
      storedAuthenticationCredentials,
      queriedAdminDebugCredentials
    );

    if (authenticationState !== AuthenticationState.Authenticated) {
      initI18n(translations, config.shared.i18n);
      changeLanguage(navigator.language as Locale);
      document.documentElement.setAttribute("lang", navigator.language);
    }

    switch (authenticationState) {
      case AuthenticationState.Registering:
        // display a blank page as long as the registration process is going on
        return null;
      case AuthenticationState.InsufficientCredentials:
        return <CredentialsNotice />;
      case AuthenticationState.RegistrationFailed:
        return <MaxLicensesReachedNotice />;
    }

    if (isAuthenticated(storedAuthenticationCredentials)) {
      setLicenseCookies(storedAuthenticationCredentials);
    }

    const { serviceTag, licenseId, licenseToken } = storedAuthenticationCredentials;
    let query = `?serviceTag=${serviceTag}`;
    // If admin debug cookie exists we don't append license params
    if (licenseId && licenseToken) {
      query += `&licenseId=${licenseId}&licenseToken=${licenseToken}`;
    }
    injectManifest(generateManifest(query));
  } else {
    injectManifest(generateManifest());
  }

  const mixpanel = initMixpanel({
    appVersion: getClientVersion(),
    sentry,
    config: config.mobile.mixpanel
  });

  sentry.configureServiceTagAsUsername();

  // Mixpanel
  const mobileMixpanelToken = envelope.isProductionTracking
    ? config.mobile.mixpanel.token.prod
    : config.mobile.mixpanel.token.dev;
  const mobileMixpanelApiHost = envelope.isProductionTracking
    ? config.mobile.mixpanel.apiHost?.prod
    : config.mobile.mixpanel.apiHost?.dev;
  mixpanel.init(mobileMixpanelToken, { apiHost: mobileMixpanelApiHost });

  initI18n(translations, config.shared.i18n, sentry);

  const trackingMiddleware = createMobileTrackingMiddleware(mixpanel);
  const store = createMobileStore({ trackingMiddleware });
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * Ensure query functions run regardless of browser-detected online/offline status.
         * This prevents queries from being paused when the browser thinks it's offline.
         * @see https://tanstack.com/query/latest/docs/framework/react/guides/network-mode#network-mode-always
         */
        networkMode: "always",
        refetchOnReconnect: true,
        retry: shouldRetryQuery
      }
    },
    mutationCache: new MutationCache({
      /**
       * Automatic query invalidation based on key matching:
       * When a mutation succeeds, invalidate any queries whose keys share elements with the mutation key.
       *
       * Example: A mutation with key ['settings', 'customization'] would invalidate queries with keys.
       * like ['settings'] but not ['products'].
       * @see https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations#tie-it-to-the-mutationkey
       */
      onSuccess: (_data, _variables, _onMutateResult, mutation) => {
        queryClient.invalidateQueries({
          predicate: query => {
            /** Find any shared elements between mutation key and query key */
            const match = intersection(mutation.options.mutationKey, query.queryKey);
            return match.length > 0;
          }
        });
      }
    })
  });

  if (!envelope.isProductionBuild) {
    // Expose store and action creators to be able to dispatch actions in console
    window.dev = {
      store,
      actions: allActions
    };
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <BrowserRouter>
          <TrackingProvider sentry={sentry} mixpanel={mixpanel}>
            <App />
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
          </TrackingProvider>
        </BrowserRouter>
      </Provider>
    </QueryClientProvider>
  );
};

export async function renderApp() {
  const container = document.getElementById("app");
  //eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Root container is defined in index.html
  const root = createRoot(container!, sentryRootOptions(sentry));
  root.render(await renderRoot());
}
